import React from "react";
import { ModalErrorBoundary } from "../ErrorBoundaryFallbacks";
import Checkbox from "../ui/Checkbox";
import { ExportReviewModal, KeyframeMismatchModal } from "./MainPanelOverlays";
import { formatApproxFileSize } from "./exportUtils";
import {
  DEPLOYMENT_URDF_DIFFERENCE_HELP_TEXT,
  ENVIRONMENT_EXPORT_ADVANCED_HELP_TEXT,
  MJCF_EXPORT_DIFFERENCE_HELP_TEXT,
} from "./constants";

const isBlankProjectReview = (review) => Boolean(review?.counts?.isBlankProject);

const formatEstimatedDownloadMeta = (bytes) =>
  Number(bytes) > 0 ? `Estimated download: ${formatApproxFileSize(bytes)}` : undefined;

const maybeEstimatedDownloadCount = (bytes) =>
  Number(bytes) > 0
    ? [
        {
          label: "Estimated download",
          value: formatApproxFileSize(bytes),
        },
      ]
    : [];

const MainPanelExportModals = React.memo(function MainPanelExportModals({
  pendingKeyframeMismatchExport,
  handleSkipMismatchedKeyframes,
  handleAbortMismatchedKeyframes,
  pendingMjcfExport,
  exportProjectName,
  setExportProjectName,
  includeMjcfDesktopScene,
  setIncludeMjcfDesktopScene,
  setPendingMjcfLiftModelToFloor,
  handleDownloadPendingMjcfExport,
  handleExportReviewFeedback,
  handleExportReviewUpdates,
  handleDownloadPendingMjcfBundle,
  handleCancelPendingMjcfExport,
  pendingEnvironmentExport,
  handleDownloadPendingEnvironmentLightBundle,
  handleDownloadPendingEnvironmentBundle,
  handleCancelPendingEnvironmentExport,
  pendingDeploymentBundle,
  handleDownloadPendingDeploymentBundle,
  handleCancelPendingDeploymentBundle,
}) {
  return (
    <>
      {pendingKeyframeMismatchExport && (
        <ModalErrorBoundary
          name="keyframe-mismatch-modal"
          title="Keyframe mismatch dialog"
          onClose={handleAbortMismatchedKeyframes}
        >
          <KeyframeMismatchModal
            pending={pendingKeyframeMismatchExport}
            onSkip={handleSkipMismatchedKeyframes}
            onAbort={handleAbortMismatchedKeyframes}
          />
        </ModalErrorBoundary>
      )}
      {pendingMjcfExport && (
        <ModalErrorBoundary
          name="mjcf-export-modal"
          title="MuJoCo export dialog"
          onClose={handleCancelPendingMjcfExport}
        >
          <ExportReviewModal
            exportName="MuJoCo Export"
            review={pendingMjcfExport.review}
            counts={[
              { label: "Bodies", value: pendingMjcfExport.review.counts?.bodies ?? 0 },
              { label: "Joints", value: pendingMjcfExport.review.counts?.joints ?? 0 },
              { label: "Actuators", value: pendingMjcfExport.review.counts?.actuators ?? 0 },
              { label: "Assets", value: pendingMjcfExport.review.counts?.referencedAssets ?? 0 },
              {
                label: "Missing assets",
                value: pendingMjcfExport.review.counts?.missingAssets ?? 0,
              },
              {
                label: "Model XML size",
                value: `${pendingMjcfExport.review.counts?.fileSizeKb ?? 0} KB`,
              },
            ]}
            helperText={MJCF_EXPORT_DIFFERENCE_HELP_TEXT}
            hideSummary
            projectName={exportProjectName}
            onProjectNameChange={setExportProjectName}
            extraContent={
              <div className="space-y-3">
                {(pendingMjcfExport.review.floorClearance?.liftAmount || 0) > 1e-6 ? (
                  <div>
                    <h3 className="mb-1 px-1 text-xs font-medium text-gray-300">Optional fixes</h3>
                    <div className="rounded-md border border-gray-600/50 bg-black/20 p-2">
                      <Checkbox
                        checked={Boolean(pendingMjcfExport.liftModelToFloor)}
                        onChange={(event) => setPendingMjcfLiftModelToFloor?.(event.target.checked)}
                        label="Lift model so collision geometry rests on the floor"
                        className="px-1 py-1 text-xs text-gray-100 hover:bg-white/5"
                      />
                      <p className="mt-1 px-1 text-xs panel-text-helper">
                        Raises exported top-level robot bodies by about{" "}
                        {pendingMjcfExport.review.floorClearance.liftAmount.toFixed(4)} m. This only
                        affects the downloaded MJCF.
                      </p>
                    </div>
                  </div>
                ) : null}
                <div>
                  <h3 className="mb-1 px-1 text-xs font-medium text-gray-300">Export options</h3>
                  <div className="rounded-md border border-gray-600/50 bg-black/20 p-2">
                    <Checkbox
                      checked={includeMjcfDesktopScene}
                      onChange={(event) => setIncludeMjcfDesktopScene(event.target.checked)}
                      label="Include desktop scene with floor and lighting"
                      className="px-1 py-1 text-xs text-gray-200 hover:bg-white/5"
                    />
                    <p className="mt-1 px-1 text-xs panel-text-helper">
                      Adds scene.xml and keeps robot.xml for reuse in other MuJoCo scenes.
                    </p>
                  </div>
                </div>
              </div>
            }
            primaryLabel="Download MuJoCo Folder"
            downloadComplete={pendingMjcfExport.downloadComplete}
            secondaryLabel={
              includeMjcfDesktopScene ? "Download Scene XML Only" : "Download Robot XML Only"
            }
            onSecondaryDownload={handleDownloadPendingMjcfExport}
            onOpenFeedback={handleExportReviewFeedback}
            onOpenUpdates={handleExportReviewUpdates}
            onDownload={handleDownloadPendingMjcfBundle}
            onCancel={handleCancelPendingMjcfExport}
          />
        </ModalErrorBoundary>
      )}
      {pendingEnvironmentExport && (
        <ModalErrorBoundary
          name="environment-export-modal"
          title="Critter project export dialog"
          onClose={handleCancelPendingEnvironmentExport}
        >
          <ExportReviewModal
            exportName="Critter Engine Project"
            review={pendingEnvironmentExport.review}
            counts={
              isBlankProjectReview(pendingEnvironmentExport.review)
                ? []
                : [
                    {
                      label: "Bodies",
                      value: pendingEnvironmentExport.review.counts?.bodies ?? 0,
                    },
                    {
                      label: "Joints",
                      value: pendingEnvironmentExport.review.counts?.joints ?? 0,
                    },
                    {
                      label: "Actuators",
                      value: pendingEnvironmentExport.review.counts?.actuators ?? 0,
                    },
                    {
                      label: "Assets",
                      value: pendingEnvironmentExport.review.counts?.referencedAssets ?? 0,
                    },
                    {
                      label: "Missing assets",
                      value: pendingEnvironmentExport.review.counts?.missingAssets ?? 0,
                    },
                    ...maybeEstimatedDownloadCount(
                      pendingEnvironmentExport.review.counts?.estimatedDownloadBytes
                    ),
                  ]
            }
            projectName={exportProjectName}
            onProjectNameChange={setExportProjectName}
            primaryLabel={
              isBlankProjectReview(pendingEnvironmentExport.review)
                ? "Save Blank Project"
                : "Download Critter Project"
            }
            downloadComplete={pendingEnvironmentExport.downloadComplete}
            secondaryLabel={
              (pendingEnvironmentExport.review.counts?.referencedAssets ?? 0) > 0
                ? "Download Without Assets (references only)"
                : undefined
            }
            secondaryTitle="Reference-only project"
            secondaryDescription={ENVIRONMENT_EXPORT_ADVANCED_HELP_TEXT}
            secondaryMeta={formatEstimatedDownloadMeta(
              pendingEnvironmentExport.review.counts?.estimatedLightDownloadBytes
            )}
            onSecondaryDownload={handleDownloadPendingEnvironmentLightBundle}
            onOpenFeedback={handleExportReviewFeedback}
            onOpenUpdates={handleExportReviewUpdates}
            onDownload={handleDownloadPendingEnvironmentBundle}
            onCancel={handleCancelPendingEnvironmentExport}
          />
        </ModalErrorBoundary>
      )}
      {pendingDeploymentBundle && (
        <ModalErrorBoundary
          name="deployment-export-modal"
          title="Deployment export dialog"
          onClose={handleCancelPendingDeploymentBundle}
        >
          <ExportReviewModal
            exportName="Deployment Bundle"
            review={pendingDeploymentBundle.readiness}
            counts={[
              { label: "Warnings", value: pendingDeploymentBundle.readiness.counts?.warnings ?? 0 },
              {
                label: "Actuators",
                value: pendingDeploymentBundle.readiness.counts?.actuators ?? 0,
              },
              {
                label: "Mimic joints",
                value: pendingDeploymentBundle.readiness.counts?.urdfMimicJoints ?? 0,
              },
              {
                label: "Ball joints",
                value: pendingDeploymentBundle.readiness.counts?.ballJoints ?? 0,
              },
              {
                label: "Collapsed DOFs",
                value: pendingDeploymentBundle.readiness.counts?.collapsedBallJointDofs ?? 0,
              },
              {
                label: "Metadata-only constraints",
                value:
                  pendingDeploymentBundle.readiness.counts?.unsupportedEqualityConstraints ?? 0,
              },
              {
                label: "Unmapped actuators",
                value: pendingDeploymentBundle.readiness.counts?.unmappedActuators ?? 0,
              },
            ]}
            helperText={DEPLOYMENT_URDF_DIFFERENCE_HELP_TEXT}
            projectName={exportProjectName}
            onProjectNameChange={setExportProjectName}
            primaryLabel="Download Bundle"
            downloadComplete={pendingDeploymentBundle.downloadComplete}
            onOpenFeedback={handleExportReviewFeedback}
            onOpenUpdates={handleExportReviewUpdates}
            onDownload={handleDownloadPendingDeploymentBundle}
            onCancel={handleCancelPendingDeploymentBundle}
          />
        </ModalErrorBoundary>
      )}
    </>
  );
});

export default MainPanelExportModals;
